class PredictiveSearchComponent extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.viewAllButton = this.querySelector('[ref="ViewAllButton"]');
    this.content = this.querySelector("[data-predictive-search]");
    this.cachedResults = {};
    this.controller = new AbortController();
    this.searchTerm = "";
    this.activeFetch = null;
  }

  connectedCallback() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.input.form.addEventListener("reset", this.onFormReset.bind(this));
    this.viewAllButton &&
      this.viewAllButton.addEventListener(
        "click",
        this.onViewAllButtonClick.bind(this)
      );
    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );
    window.addEventListener("load", this.renderViewedProducts.bind(this));
  }

  reset() {
    this.input.form.reset();
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    this.toggleResetButton();
    const newSearchTerm = this.getQuery();
    this.searchTerm = newSearchTerm;
    if (this.searchTerm.length == 0) {
      this.onFormReset();
      return;
    }
    this.getSearchResults();
  }

  onFormSubmit(event) {
    event.preventDefault();
  }

  onFormReset(event) {
    event && event.preventDefault();
    this.input.value = "";
    this.input.focus();
    this.toggleResetButton();
    this.resetSearch();
  }

  onViewAllButtonClick(event) {
    event && event.preventDefault();
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set("type", "product");
    url.searchParams.set("q", this.searchTerm);
    window.location.href = url.href;
  }

  resetSearch = async () => {
    const abortController = new AbortController();
    const el = document.createElement("div");
    const emptySectionMarkup = await this.getEmptyStateMarkup();
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, "text/html")
      .getElementById("predictive-search-empty-state");
    parsedEmptySectionMarkup && el.append(parsedEmptySectionMarkup);

    const recentlyViewedMarkup = await this.getRecentlyViewedProductsMarkup();
    const parsedRecentlyViewedMarkup = new DOMParser()
      .parseFromString(recentlyViewedMarkup, "text/html")
      .getElementById("predictive-search-results");
    recentlyViewedMarkup && el.prepend(parsedRecentlyViewedMarkup);

    if (abortController.signal.aborted) return;

    this.content.replaceChildren(el);
  };

  toggleResetButton() {
    if (this.input.value.length > 0) {
      if (this.resetButton.classList.contains("hidden")) {
        this.resetButton.classList.remove("hidden");
        this.resetButton.classList.add("flex");
      }
    } else {
      this.resetButton.classList.remove("flex");
      this.resetButton.classList.add("hidden");
    }
  }

  getSearchUrl() {
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set("q", this.searchTerm);
    url.searchParams.set("resources[limit_scope]", "each");
    url.searchParams.set("resources[limit]", 8);
    url.searchParams.set("section_id", "predictive-search");
    return url;
  }

  getSearchResults() {
    if (!this.searchTerm) return;

    const queryKey = this.searchTerm.replace(" ", "-").toLowerCase();
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(queryKey);
      return;
    }

    fetch(this.getSearchUrl(), { signal: this.controller.signal })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          throw error;
        }
        return response.text();
      })
      .then((responseText) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(responseText, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(queryKey);
      })
      .catch((error) => {
        if (error.code == 20) return;
        throw error;
      });
  }

  renderSearchResults(queryKey) {
    this.content.innerHTML = this.cachedResults[queryKey];
  }

  async getEmptyStateMarkup() {
    const url = new URL(location.href, location.origin);
    url.searchParams.set("section_id", "predictive-search-empty");

    const promise = fetch(url).then((response) => {
      return response.text();
    });

    const sectionHTML = await promise;
    return sectionHTML;
  }

  renderViewedProducts = async () => {
    const abortController = new AbortController();

    const resultsMarkup = await this.getRecentlyViewedProductsMarkup();
    if (!resultsMarkup) return;

    const parsedNextPage = new DOMParser().parseFromString(
      resultsMarkup,
      "text/html"
    );
    const recentlyViewedProductsHtml = parsedNextPage.getElementById(
      "predictive-search-results"
    );
    if (!recentlyViewedProductsHtml) return;

    const collectionElement = this.content.querySelector(
      "#predictive-search-empty-state"
    );
    if (!collectionElement) return;

    if (abortController.signal.aborted) return;

    this.content.prepend(...recentlyViewedProductsHtml.children);

    const clearViewsProductsButton = this.content.querySelector('button[ref="recentlyViewedClear"]');
    if (clearViewsProductsButton) {
      clearViewsProductsButton.addEventListener('click', this.clearViewedProducts.bind(this));
    }
  };

  clearViewedProducts(event) {
    event.preventDefault();
    window.RecentlyViewed.clearProducts();
  }

  async getRecentlyViewedProductsMarkup() {
    const viewedProducts = window.RecentlyViewed.getProducts();
    if (viewedProducts.length < 1) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set(
      "q",
      viewedProducts.map((id) => `id:${id}`).join(" OR ")
    );
    url.searchParams.set("resources[type]", "product");
    url.searchParams.set("section_id", "predictive-search");

    const promise = fetch(url).then((response) => {
      return response.text();
    });

    const sectionHTML = await promise;
    return sectionHTML;
  }
}
customElements.define("predictive-search-component", PredictiveSearchComponent);
